import React, { useState, useEffect } from 'react';
import { fetchCategories, fetchSubcategories, fetchCompanies } from '../services/api';
import { toast } from 'react-toastify';

const JobFormModal = ({ job, onClose, onSave }) => {
  const isEditMode = !!job;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    location: '',
    status: 'ACTIVE',
    isActive: true,
    companyId: '',
    jobRequirement: '',
    jobType: 'Full Time'
  });
  
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'CLOSED', label: 'Closed' }
  ];

  const jobTypeOptions = [
    { value: 'Full Time', label: 'Full Time' },
    { value: 'Part Time', label: 'Part Time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Freelance', label: 'Freelance' },
    { value: 'Internship', label: 'Internship' }
  ];

  // Initialize form data when editing
  useEffect(() => {
    if (isEditMode && job) {
      setFormData({
        title: job.title || '',
        description: job.description || '',
        category: job.category || '',
        subcategory: job.subcategory || '',
        location: job.location || '',
        status: job.status || 'ACTIVE',
        isActive: job.isActive,
        companyId: job.companyId || job.createdBy?.id || '',
        jobRequirement: job.jobRequirement || '',
        jobType: job.jobType || 'Full Time'
      });
    }
  }, [job, isEditMode]);

  // Load categories, subcategories, and companies on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [categoriesData, companiesData] = await Promise.all([
          fetchCategories(),
          fetchCompanies({ size: 100 })
        ]);
        
        setCategories(categoriesData);
        setCompanies(companiesData.content || []);
        
        // If we're in edit mode, find the category ID by name
        if (isEditMode && job?.category) {
          const categoryObj = categoriesData.find(cat => cat.name === job.category);
          if (categoryObj) {
            setSelectedCategoryId(categoryObj.id.toString());
            // Load subcategories for the found category
            loadSubcategoriesForCategory(categoryObj.id);
          }
        }
      } catch (error) {
        toast.error('Failed to load form data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditMode, job]);

  // Function to load subcategories for a specific category ID
  const loadSubcategoriesForCategory = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchSubcategories(categoryId);
      setSubcategories(data || []);
    } catch (error) {
      toast.error('Failed to fetch subcategories');
      setSubcategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'category') {
      // Handle category change
      const categoryId = value;
      setSelectedCategoryId(categoryId);
      
      // Find the category name from the ID
      const categoryObj = categories.find(cat => cat.id.toString() === categoryId);
      const categoryName = categoryObj ? categoryObj.name : '';
      
      setFormData({
        ...formData,
        category: categoryName,
        subcategory: '' // Reset subcategory when category changes
      });
      
      // Load subcategories for the selected category
      loadSubcategoriesForCategory(categoryId);
    } else if (name === 'subcategory') {
      // Handle subcategory change
      const subcategoryId = value;
      
      // Find the subcategory name from the ID
      const subcategoryObj = subcategories.find(subcat => subcat.id.toString() === subcategoryId);
      const subcategoryName = subcategoryObj ? subcategoryObj.name : '';
      
      setFormData({
        ...formData,
        subcategory: subcategoryName
      });
    } else {
      // Handle other fields normally
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    
    // Clear any error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.subcategory) {
      newErrors.subcategory = 'Subcategory is required';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    if (!formData.companyId) {
      newErrors.companyId = 'Company is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {isEditMode ? 'Update Job' : 'Create Job'}
                </h3>
                <div className="mt-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Job Title */}
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                        Job Title*
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        value={formData.title}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Job Description*
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows="4"
                        value={formData.description}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      ></textarea>
                      {errors.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                      )}
                    </div>

                    {/* Job Requirements */}
                    <div>
                      <label htmlFor="jobRequirement" className="block text-sm font-medium text-gray-700">
                        Job Requirements
                      </label>
                      <textarea
                        name="jobRequirement"
                        id="jobRequirement"
                        rows="3"
                        value={formData.jobRequirement}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter job requirements, qualifications, skills needed, etc."
                      ></textarea>
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                        Company*
                      </label>
                      <select
                        name="companyId"
                        id="companyId"
                        value={formData.companyId}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.companyId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        disabled={isLoading}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                      {errors.companyId && (
                        <p className="mt-1 text-sm text-red-600">{errors.companyId}</p>
                      )}
                    </div>

                    {/* Category and Subcategory */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category*
                        </label>
                        <select
                          name="category"
                          id="category"
                          value={selectedCategoryId}
                          onChange={handleChange}
                          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                            errors.category ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                          disabled={isLoading}
                        >
                          <option value="">Select Category</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        {errors.category && (
                          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">
                          Subcategory*
                        </label>
                        <select
                          name="subcategory"
                          id="subcategory"
                          value={subcategories.find(s => s.name === formData.subcategory)?.id || ''}
                          onChange={handleChange}
                          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                            errors.subcategory ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                          disabled={!selectedCategoryId || isLoading}
                        >
                          <option value="">Select Subcategory</option>
                          {subcategories.map((subcat) => (
                            <option key={subcat.id} value={subcat.id}>
                              {subcat.name}
                            </option>
                          ))}
                        </select>
                        {errors.subcategory && (
                          <p className="mt-1 text-sm text-red-600">{errors.subcategory}</p>
                        )}
                      </div>
                    </div>

                    {/* Location, Job Type, and Status */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                          Location
                        </label>
                        <input
                          type="text"
                          name="location"
                          id="location"
                          value={formData.location}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="e.g., Remote, New York, etc."
                        />
                      </div>

                      <div>
                        <label htmlFor="jobType" className="block text-sm font-medium text-gray-700">
                          Job Type*
                        </label>
                        <select
                          name="jobType"
                          id="jobType"
                          value={formData.jobType}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          {jobTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status*
                        </label>
                        <select
                          name="status"
                          id="status"
                          value={formData.status}
                          onChange={handleChange}
                          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                            errors.status ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                        >
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.status && (
                          <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                        )}
                      </div>
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center">
                      <input
                        id="isActive"
                        name="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                        Active Job
                      </label>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {isEditMode ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobFormModal; 